import ChartAccordion from "@components/dataDisplay/chartAccordion/ChartAccordion"
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import styles from "./chart-code.module.css"
// import buttonStyles from "@styles/button.module.css"
import Button from "@components/general/button/Button"
import throttle from "lodash/throttle"
import type { GetServerSideProps, NextPage } from "next"
import AppLayout from "@components/layout/AppLayout"

type ItemRow = {
  id: number
  name: string
  variation?: string
  symbol: string
  currency: string
  type: string
}

type Response = {
  status: "success" | "error" | "undefined"
  message?: string
  data?: ItemRow[]
}

const DeleteButton = ({
  handleDeleteButtonClick,
  item,
}: {
  handleDeleteButtonClick: (item: ItemRow) => void
  item: ItemRow
}) => (
  <button onClick={() => handleDeleteButtonClick(item)}>
    <svg width="10px" height="10px" viewBox="0 0 4 4">
      <g transform="matrix(1,0,0,1,0,0)">
        <g transform="matrix(0.0143266,0,0,0.0138504,-1.76218,-0.941828)">
          <g transform="matrix(87.25,0,0,90.25,35.75,-22.25)">
            <path
              d="M3.744,1.744L4.256,2.256L3.512,3L4.256,3.744L3.744,4.256L3,3.512L2.256,4.256L1.744,3.744L2.488,3L1.744,2.256L2.256,1.744L3,2.488L3.744,1.744Z"
              fill="#B7B7B7"
            />
          </g>
        </g>
      </g>
    </svg>
  </button>
)

type Props = { host: string }

const ChartCode: NextPage<Props> = ({ host }) => {
  const [quotesData, setQuotesData] = useState({})
  const [value, setValue] = useState("")
  const [items, setItems] = useState<ItemRow[]>([])
  const [data, setData] = useState<Response>({ status: "undefined", data: [] })
  const [hasFocus, setHasFocus] = useState(false)

  const endpoint = process.env.WP_ENDPOINT ?? "https://wp.fuw.ch/wp-json/fuw/v1"

  const fetchData = useCallback(
    async (value: string) => {
      const url = `${endpoint}/marketdata/search?query=${value}&dataSource=fuw`
      if (value) {
        const request = await fetch(url, {
          method: "GET",
        })
        const json = await request.json()
        const response = json as Response
        setData(response)
      }
    },
    [endpoint],
  )

  const throttledFetch = useMemo(() => throttle(fetchData, 1500), [fetchData])

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    throttledFetch(e.target.value)
    setValue(e.target.value)
  }

  const handleDeleteButtonClick = (clickedItem: ItemRow) => {
    setItems(items.filter((item) => item.name !== clickedItem.name))
  }

  const handleDropdownClick = async (clickedItem: ItemRow) => {
    const itemNames = items.map((item) => item.name)

    if (!itemNames.includes(clickedItem.name)) {
      setItems([...items, clickedItem])
      setValue("")
      setHasFocus(false)
    }
  }

  useEffect(() => {
    const fetchQuotesData = async () => {
      if (items.length !== 0) {
        const url = `${endpoint}/marketdata/quotes/${items.map((item) => item.id).join(",")}`
        console.log(url)
        const quotesRes = await fetch(url)
        const res = await quotesRes.json()
        setQuotesData(res)
      }
    }
    fetchQuotesData()
  }, [endpoint, items])

  const url = useMemo(() => `${host}/embed/chart?ids=${items.map((item) => item.id).join(",")}`, [host, items])
  const code = useMemo(
    () => `<iframe src='${url}'  scrolling='no' frameBorder='0' style='width: 100%; height: auto'></iframe>`,
    [url],
  )

  return (
    <AppLayout title="Chart Code Tool" robots="noindex,nofollow">
      <div style={{ margin: 20 }}>
        <label className={styles.label} htmlFor="search">
          Suche
        </label>
        <div className={styles.dropdownContainer}>
          <input
            autoComplete="off"
            id="search"
            className={styles.input}
            value={value}
            onChange={(e) => handleInput(e)}
            onClick={() => setHasFocus(true)}
            onFocus={() => setHasFocus(true)}
            onBlur={() => setHasFocus(false)}
            type="search"
          />
          {hasFocus && value !== "" && data.data?.length !== 0 && (
            <div className={styles.dropdown}>
              {data.data?.map((item: ItemRow, j) => (
                <div
                  key={j}
                  onMouseDown={(e) => e.preventDefault()} // Prevent blur before calling click: https://stackoverflow.com/questions/17769005/onclick-and-onblur-ordering-issue/57630197#57630197
                  onClick={() => handleDropdownClick(item)}
                >
                  <div className={styles.dropdownRow}>
                    <div>
                      <div>
                        <span className={styles.dropdownName}>{item.name}</span>
                        <span className={styles.dropdownSymbol}>{item.symbol}</span>
                      </div>
                      {item.variation && <div className={styles.dropdownVariation}>{item.variation}</div>}
                    </div>
                    <div>
                      <div className={styles.dropdownCurrency}>{item.currency}</div>
                      <div className={styles.dropdownExchange}>{item.type}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={styles.tagContainer}>
          {items.map((item, i) => (
            <div key={i} className={styles.tag}>
              {item.name}
              <DeleteButton handleDeleteButtonClick={handleDeleteButtonClick} item={item} />
            </div>
          ))}
        </div>
        {items.length !== 0 && (
          <div className={styles.urlContainer}>
            <textarea className={styles.urlTextarea} readOnly={true} value={code} />
            <Button type="button" onClick={() => navigator.clipboard.writeText(code)}>
              Copy Code
            </Button>
            <Button type="link" href={url}>
              Open Link
            </Button>
          </div>
        )}
        {quotesData && items.length !== 0 && <ChartAccordion quotesData={quotesData} />}
      </div>
    </AppLayout>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const protocol = context.req.headers["x-forwarded-proto"] || "http"
  return {
    props: { host: `${protocol}://${context.req.headers.host}` },
  }
}

export default ChartCode
